import React, { PureComponent } from 'react'
import { Grid } from '@material-ui/core'
import PropTypes from 'prop-types'

// components
import { Button, Modal, Message, RenderHTML, Spinner } from '../../../../components'
import ContactSearch from './search/search.component'

import { combine, MESSAGE, THEMES } from '../../../../utils'
import { statusType } from '../../../../actions'

import './match.component.scss'

const fields = [
  { label: 'First Name', value: 'first_name' },
  { label: 'Last Name', value: 'last_name' },
  { label: 'Nickname', value: 'nickname' },
  { label: 'Institution', value: 'institution_name' },
  { label: 'Job Title', value: 'title' },
  { label: 'Corporate Phone', value: 'phone' },
  { label: 'Corporate Email', value: 'email' },
  { label: 'Biography', value: 'bio' }
]

/**
 * Confirm Modal
 * @param props
 * @returns {*}
 * @constructor
 */
const ConfirmModal = ({ onClose, onConfirm }) => {
  return (
    <Message
      visible
      type='warning'
      title='Remove Duplicate'
      message='All customer data (activities, comments and tags) will be linked to the master contact. The custom contact will be removed permanently. This action cannot be undone. Are you sure?'
      buttons={[
        {
          theme: 'shaded',
          label: 'cancel',
          onClick: onClose
        },
        {
          theme: 'citrus',
          label: 'confirm',
          onClick: () => onConfirm()
        }
      ]}
    />
  )
}

/**
 * Error Message Modal
 * @param onClose
 * @returns {*}
 * @constructor
 */
const ErrorModal = ({ onClose }) => {
  return (
    <Message
      visible
      type='error'
      title='Error'
      message={MESSAGE.ERROR}
      onClose={onClose}
      buttons={[{
        theme: 'spice',
        label: 'close',
        onClick: onClose
      }]}
    />
  )
}

/**
 * Contact Match Preview Component
 */
class ContactMatch extends PureComponent {
  /**
   * Constructor
   * @param props
   */
  constructor (props) {
    super(props)
    this.state = {
      master: (props.contact && props.contact._match) || null,
      isContactSearch: false,
      isMerging: false
    }
  }

  /**
   * Component Did Update
   * @param prevProps
   * @param prevState
   */
  componentDidUpdate (prevProps, prevState) {
    const { mergeStatus } = this.props
    const { mergeStatus: prevMergeStatus } = prevProps

    if (mergeStatus !== prevMergeStatus) {
      this.setState({ isMerging: mergeStatus === statusType.IN_PROGRESS })
    }
  }

  /**
   * On Change Button Click
   */
  onContactChange () {
    this.setState({ isContactSearch: true })
  }

  /**
   * Get contact fields
   * @param contact
   * @param source
   */
  getContact (contact, source) {
    return (fields || []).map((field) => {
      const value = contact && contact[field.value]
      const bio = !!(field.value === 'bio' && value && value.length)
      const job = (contact && contact.jobs && contact.jobs.length && contact.jobs[0]) || null

      return (
        <div key={`${field.value}-${source}`} className='match_detail-row'>
          <div className={`match_detail-item ${bio ? 'match_detail-item--biography' : ''}`}>
            {bio
              ? <RenderHTML html={value.replace(/\\r|\\n/g, '<br>')} />
              : value || (job && job[field.value]) || '-'}
          </div>
        </div>
      )
    })
  }

  /**
   * On Custom Contact Duplicate Remove
   */
  onRemoveDuplicate () {
    const { openModal } = this.props
    const { master } = this.state

    if (!master || !master._id) {
      return
    }

    openModal({
      component: ({ onClose }) => ConfirmModal({
        onClose, onConfirm: this.onRemoveDuplicateConfirm.bind(this)
      })
    })
  }

  /**
   * Remove Custom Contact Duplicate
   */
  onRemoveDuplicateConfirm () {
    const { mergeContact, contact } = this.props
    const { master } = this.state
    const id = contact && contact._id
    const masterId = master && master._id

    this.handleMessageClose()

    mergeContact({ id, masterId })
      .then(this.onRemoveSuccess.bind(this))
      .catch(this.handleError.bind(this))
  }

  /**
   * On Remove Success
   * Create toast and close modal component
   */
  onRemoveSuccess () {
    const { createToast, onClose, onSuccess } = this.props
    createToast({ text: 'Contact merged successfully.' })

    onClose && onClose()
    onSuccess && onSuccess()
  }

  /**
   * Handle close message modal
   * @private
   */
  handleMessageClose () {
    const { closeModal } = this.props
    closeModal({
      component: ConfirmModal
    })
  }

  /**
   * Handle request error
   * @private
   */
  handleError () {
    const { openModal } = this.props
    openModal({
      component: ErrorModal
    })
  }

  /**
   * Render
   * @returns {*}
   */
  render () {
    const {
      contact, onClose, loadingSuggestions, suggestions, searchContacts, clearSearchResults
    } = this.props
    const { master, isContactSearch, isMerging } = this.state

    const buttons = [
      {
        label: 'Cancel',
        ui: 'shaded',
        onClick: onClose
      },
      {
        label: 'Remove Duplicate',
        ui: 'citrus',
        disabled: !(master && master._id),
        onClick: this.onRemoveDuplicate.bind(this)
      }
    ]

    return (
      <>
        <Modal
          visible
          fullscreen
          title='Preview Contact Match'
          subtitle='When you merge, the custom contact will be changed to the master record. All tags, activities, and comments will be retained.'
          contentPadding='0'
          buttons={buttons}
          onClose={onClose}>
          <div className='match'>
            {isMerging && <Spinner fixed mask />}
            <Grid container justify='flex-end' className='match_header'>
              <Grid item xs={4} className='match_header-item'>
                <div className='match_header-item--name'>
                  {(contact && contact._id) ? combine([contact.first_name, contact.last_name], ' ') : '-'}
                </div>
                <div className='match_header-item--label'>Custom Contact</div>
              </Grid>
              <Grid item xs={4} className='match_header-item'>
                <div className='match_header-item--name'>
                  {(master && master._id) ? combine([master.first_name, master.last_name], ' ') : '-'}
                </div>
                <div className='match_header-item--label'>Master Record Match</div>
              </Grid>
              <Button
                className='match_header-action'
                theme={THEMES.STEEL}
                label='Change'
                onClick={this.onContactChange.bind(this)} />
            </Grid>
            <Grid container className='match_detail'>
              <Grid item xs={4} className='match_detail-column'>
                {(fields || []).map((field) => {
                  return (
                    <div key={`${field.value}-label`} className='match_detail-row'>
                      <div className='match_detail-item match_detail-item--label'>{field.label}</div>
                    </div>
                  )
                })}
              </Grid>
              <Grid item xs={4} className='match_detail-column'>
                {this.getContact(contact, 'contact')}
              </Grid>
              <Grid item xs={4}>
                {this.getContact(master, 'master')}
              </Grid>
            </Grid>
          </div>
        </Modal>

        {isContactSearch && <ContactSearch
          _organizations={contact._organization}
          loading={loadingSuggestions}
          suggestions={suggestions}
          searchContacts={searchContacts}
          clearSearchResults={clearSearchResults}
          onClose={() => this.setState({ isContactSearch: false })}
          onMasterUpdate={(contact) => this.setState({
            master: contact, isContactSearch: false
          })} />}
      </>
    )
  }
}

ContactMatch.propTypes = {
  contact: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  loadingSuggestions: PropTypes.bool,
  suggestions: PropTypes.array,
  searchContacts: PropTypes.func.isRequired,
  clearSearchResults: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  createToast: PropTypes.func.isRequired,
  mergeContact: PropTypes.func.isRequired
}

ContactMatch.defaultProps = {
  contact: {}
}

export default ContactMatch
